import React, { useState, useEffect } from 'react';
import supabase from '../../supabaseClient';
import { AddClientModal } from './AddClientModal';
import ClientPrivacyModal from '../../ClientPrivacyModal';
import ClientMessaging from '../communication/ClientMessaging';
import { usePremiumAccess } from '../../hooks/usePremiumAccess';
import { filterToAllowedClients, getClientAccessInfo } from '../../utils/clientAccess';
import { NavigationItem } from '../navigation/BottomNavigation';

interface ClientsViewProps {
  onClientSelect: (userId: string, clientName: string) => void;
  selectedUserId?: string;
  refreshTrigger: number;
  onNavigate?: (view: NavigationItem) => void;
}

export function ClientsView({ onClientSelect, selectedUserId, refreshTrigger, onNavigate }: ClientsViewProps) {
  const [clients, setClients] = useState<any[]>([]);
  const [lockedCount, setLockedCount] = useState(0);
  const { isPremium } = usePremiumAccess();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [selectedClientForPrivacy, setSelectedClientForPrivacy] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientForMessaging, setSelectedClientForMessaging] = useState<any>(null);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);

  const fetchClients = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('trainers_clients')
        .select('*')
        .eq('trainer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        return;
      }

      const { allowedTelegramIds, lockedCount: locked } = await getClientAccessInfo(user.id, isPremium);
      const visibleClients = filterToAllowedClients(data || [], allowedTelegramIds);
      visibleClients.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setClients(visibleClients);
      setLockedCount(locked);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (telegramUserId: string, clientName: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if client already exists
      const { data: existingClient } = await supabase
        .from('trainers_clients')
        .select('*')
        .eq('trainer_id', user.id)
        .eq('telegram_user_id', telegramUserId)
        .single();

      if (existingClient) {
        alert('This client is already assigned to you.');
        return;
      }

      // Add the client
      const { error } = await supabase
        .from('trainers_clients')
        .insert({ 
          trainer_id: user.id, 
          telegram_user_id: telegramUserId, 
          client_name: clientName 
        });

      if (error) throw error;

      setIsAddModalOpen(false);
      fetchClients();
    } catch (error: any) {
      console.error('Error adding client:', error);
      alert(error.message || 'Failed to add client');
    }
  };

  useEffect(() => {
    fetchClients();
  }, [refreshTrigger, isPremium]);

  const filteredClients = clients.filter(client =>
    client.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.telegram_user_id?.includes(searchTerm)
  );

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header */}
      <div className="retro-card p-4 md:p-6">
        <div className="index-tab">
          <h2 className="text-xl font-bold text-text-dark">Client Management</h2>
        </div>
        <div className="p-3 md:p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search clients by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="vintage-input w-full"
              />
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="neon-button success whitespace-nowrap"
            >
              + Add Client
            </button>
          </div>
        </div>
      </div>

      {lockedCount > 0 && (
        <button
          type="button"
          onClick={() => onNavigate?.('subscription')}
          className="retro-card p-4 md:p-5 w-full text-left flex items-start gap-3 hover:border-[#ffd700] border-2 border-[#ffd700]/40 bg-[#ffd700]/10 transition-colors"
        >
          <span className="text-xl">🔒</span>
          <div className="flex-1">
            <p className="font-semibold text-[#1f1f1f]">
              {lockedCount} {lockedCount === 1 ? 'client' : 'clients'} hidden
            </p>
            <p className="text-sm text-[#666] mt-1">
              Your free plan only shows your first 5 clients. Upgrade to Early Supporter to see all of them again.
            </p>
          </div>
          <span className="text-[#666] text-xl">›</span>
        </button>
      )}

      {/* Client List */}
      <div className="retro-card p-4 md:p-6">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-text-dark">Your Clients</h3>
        </div>
        <div className="p-3 md:p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="loading mx-auto mb-4"></div>
              <p className="text-text-medium">Loading clients...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-semibold text-text-dark mb-2">
                {searchTerm ? 'No clients found' : 'No clients yet'}
              </h3>
              <p className="text-text-medium mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : 'Add your first client to get started'
                }
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="neon-button success"
                >
                  Add Your First Client
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className={`
                    p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer
                    ${selectedUserId === client.telegram_user_id
                      ? 'border-neon-blue bg-neon-blue bg-opacity-10 shadow-lg'
                      : 'border-gray-200 hover:border-neon-blue hover:bg-paper-cream'
                    }
                  `}
                  onClick={() => onClientSelect(client.telegram_user_id, client.client_name || 'Unknown Client')}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-text-dark mb-1">
                        {client.client_name || 'Unknown Client'}
                      </h4>
                      <p className="text-sm text-text-medium">
                        ID: {client.telegram_user_id}
                      </p>
                      <p className="text-xs text-text-light mt-2">
                        Added: {new Date(client.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {selectedUserId === client.telegram_user_id && (
                        <span className="text-neon-blue text-xl">✓</span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClientForMessaging(client);
                          setIsMessagingModalOpen(true);
                        }}
                        className="p-2 text-[#0eeaff] hover:text-[#1f1f1f] hover:bg-[#0eeaff] rounded transition-colors text-lg font-bold"
                        title="Send Message"
                      >
                        💬
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClientForPrivacy(client);
                          setIsPrivacyModalOpen(true);
                        }}
                        className="p-2 text-[#0eeaff] hover:text-[#1f1f1f] hover:bg-[#0eeaff] rounded transition-colors text-lg font-bold"
                        title="Privacy & Profile Settings"
                      >
                        ⚙️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddClient}
        loading={loading}
      />

      {/* Privacy Settings Modal */}
      {selectedClientForPrivacy && (
        <ClientPrivacyModal
          isOpen={isPrivacyModalOpen}
          onClose={() => {
            setIsPrivacyModalOpen(false);
            setSelectedClientForPrivacy(null);
          }}
          telegramUserId={selectedClientForPrivacy.telegram_user_id}
          clientName={selectedClientForPrivacy.client_name || 'Unnamed Client'}
          onProfileUpdated={() => {
            // Refresh client list so name changes propagate across the app
            fetchClients();
          }}
          onUpgrade={() => onNavigate?.('subscription')}
        />
      )}

      {/* Messaging Modal */}
      {selectedClientForMessaging && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-text-dark">
                Communication with {selectedClientForMessaging.client_name}
              </h2>
              <button
                onClick={() => {
                  setIsMessagingModalOpen(false);
                  setSelectedClientForMessaging(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <ClientMessaging
              selectedUserId={selectedUserId || ''}
              clientName={selectedClientForMessaging.client_name}
              telegramUserId={selectedClientForMessaging.telegram_user_id}
              onUpgrade={() => onNavigate?.('subscription')}
            />
          </div>
        </div>
      )}
    </div>
  );
} 